/**
 * ReadingProgressBar Component
 *
 * A reading progress indicator that shows how much of an article has been read.
 * Progress is tracked from the bottom of the viewport, which shows higher progress
 * percentages earlier - this motivates readers by making them feel "almost done"
 * sooner, encouraging them to finish the article.
 *
 * Design specs (from Figma):
 * - Height: 4px (h-1)
 * - Background: neutral/secondary
 * - Progress fill: Gradient from blue-300 → blue-500 → blue-700 as progress increases
 * - Position: fixed below FORTUNE logo header (not below nav, which scrolls away)
 * - Forward-only: Progress never decreases when scrolling back up
 *
 * Performance optimizations:
 * - Uses CSS transforms (scaleX) instead of width for no layout shifts
 * - Throttled scroll handler via requestAnimationFrame
 */
'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';
import { cn } from '@/app_lib/utils/utils';

const progressBarVariants = tv({
  slots: {
    // Transform uses CSS custom property --progress set via style prop
    bar: 'h-full origin-left transition-transform duration-75 ease-out [transform:scaleX(var(--progress))]',
    container:
      'sticky top-[calc(var(--leaderboard-height)+var(--header-height))] left-0 right-0 w-full h-1 bg-secondary z-body-overlay',
  },
  variants: {
    // Gradient based on progress percentage using CSS variables
    // At 0-50%: blue-300 to blue-500
    // At 50-100%: blue-300 → blue-500 → blue-700
    gradient: {
      high: 'bg-[linear-gradient(to_right,var(--color-blue-300),var(--color-blue-500),var(--color-blue-700))]',
      low: 'bg-[linear-gradient(to_right,var(--color-blue-300),var(--color-blue-500))]',
    },
  },
});

interface ProgressBarProps {
  className?: string;
}

export const ProgressBar = ({ className }: ProgressBarProps) => {
  const [percentage, setPercentage] = useState<number>(0);
  const maxPercentageRef = useRef<number>(0);
  const { bar, container } = progressBarVariants();

  const handleScrollProgress = useCallback(() => {
    const article = document.querySelector('article');
    const authorBio = document.getElementById('author-bio');

    if (!article) {
      return;
    }

    const articleRect = article.getBoundingClientRect();
    const articleTop = articleRect.top + window.scrollY;

    // End point is where author bio starts (end of article content), or article bottom as fallback
    const articleEnd = authorBio
      ? authorBio.getBoundingClientRect().top + window.scrollY
      : articleRect.bottom + window.scrollY;

    // Track from bottom of viewport - shows higher progress earlier to motivate readers
    // (feels like "almost done" sooner, encouraging them to finish)
    const viewportBottom = window.scrollY + window.innerHeight;

    // Don't show progress until article enters viewport
    if (viewportBottom < articleTop) {
      return;
    }

    // Calculate progress: 0% when bottom of screen hits article top, 100% at article end
    const articleHeight = articleEnd - articleTop;
    const scrolledInArticle = viewportBottom - articleTop;
    const scrollPercentage = Math.min((scrolledInArticle / articleHeight) * 100, 100);
    const newPercentage = Math.max(0, scrollPercentage);

    // Forward-only: only update if new percentage is higher than max reached
    if (newPercentage > maxPercentageRef.current) {
      maxPercentageRef.current = newPercentage;
      setPercentage(newPercentage);
    }
  }, []);

  useEffect(() => {
    // Calculate initial progress on mount
    handleScrollProgress();

    // Throttle scroll events for better performance
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScrollProgress();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScrollProgress]);

  return (
    <div
      aria-hidden='true'
      className={cn(container(), className)}
      data-cy='reading-progress-bar'
    >
      <div
        className={cn(bar(), progressBarVariants.variants.gradient[percentage <= 50 ? 'low' : 'high'])}
        style={{ '--progress': percentage / 100 } as React.CSSProperties}
      />
    </div>
  );
};
